import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Select, Space, Typography, Card, Spin, Tag, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import iconDashboard from '../../icons/dashboard.png';
import iconFarm      from '../../icons/farm.png';
import iconPlot      from '../../icons/plot.png';
import iconCrop      from '../../icons/crop.png';
import iconAccount   from '../../icons/account.png';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  LineChart, Line, CartesianGrid, ReferenceLine,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getRecentCropsApi, getDashboardCountApi } from '../../api/farms';
import { formatCurrency } from '../../utils/formatters';
import { CROP_PHOTO, CROP_NAME_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

function getUserId() {
  try { const u = JSON.parse(localStorage.getItem('user')); return u?.parent_id ?? u?.id; }
  catch { return null; }
}

const currentYear = dayjs().year();

const CHART_COLORS = { expense: '#fa8c16', sales: '#52c41a', profit: '#1677ff' };

function shortInr(v) {
  const n = Math.abs(v);
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>
      {label && <div style={{ color: '#888', marginBottom: 3, fontWeight: 600 }}>{label}</div>}
      {payload.map((p) => {
        const isLoss = p.name === 'Profit' && p.value < 0;
        return (
          <div key={p.name} style={{ color: isLoss ? '#ff4d4f' : p.color }}>
            {isLoss ? 'Loss' : p.name}: {shortInr(Math.abs(p.value))}
          </div>
        );
      })}
    </div>
  );
}

const CARD_STYLE = { borderRadius: 12, border: '1px solid #f0f0f0', background: '#fafafa', height: '100%' };
const CARD_BODY  = { padding: '10px 10px 8px' };

function ChartLabel({ text }) {
  return <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textAlign: 'center', marginBottom: 2, letterSpacing: 0.2 }}>{text}</div>;
}

function ExpenseSalesChart({ years, finance, expense, sales }) {
  const hasData = expense > 0 || sales > 0;

  if (years.length === 1) {
    const data = [
      { name: 'Expense', value: expense, color: CHART_COLORS.expense },
      { name: 'Sales',   value: sales,   color: CHART_COLORS.sales   },
    ].filter((d) => d.value > 0);
    return (
      <Card style={CARD_STYLE} styles={{ body: CARD_BODY }}>
        <ChartLabel text="Expense vs Sales" />
        {hasData ? (
          <>
            <ResponsiveContainer width="100%" height={118}>
              <PieChart>
                <Pie data={data} cx="50%" cy="50%" innerRadius={33} outerRadius={52} dataKey="value" paddingAngle={4}>
                  {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              {data.map((d) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#555' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                  <span>{d.name}</span>
                  <span style={{ color: d.color, fontWeight: 600 }}>{shortInr(d.value)}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ height: 118, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 12 }}>No data</div>
        )}
      </Card>
    );
  }

  const barData = (finance || []).map((f) => ({
    year:    String(f.year),
    Expense: Number(f.total_expense) || 0,
    Sales:   Number(f.total_sales)   || 0,
  }));
  return (
    <Card style={CARD_STYLE} styles={{ body: CARD_BODY }}>
      <ChartLabel text="Expense vs Sales" />
      {barData.length > 0 ? (
        <ResponsiveContainer width="100%" height={148}>
          <BarChart data={barData} barSize={16} barGap={0} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
            <XAxis dataKey="year" tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={shortInr} tick={{ fontSize: 9 }} domain={[0, 'auto']} />
            <Tooltip content={<ChartTooltip />} />
            <Legend iconSize={7} wrapperStyle={{ fontSize: 10, paddingTop: 2 }} />
            <Bar dataKey="Expense" fill={CHART_COLORS.expense} radius={[3,3,0,0]} minPointSize={3} />
            <Bar dataKey="Sales"   fill={CHART_COLORS.sales}   radius={[3,3,0,0]} minPointSize={3} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ height: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 12 }}>No data</div>
      )}
    </Card>
  );
}

function ProfitLineChart({ finance }) {
  const data = (finance || []).map((f) => ({
    year:   String(f.year),
    Profit: Number(f.profit) || 0,
  }));

  const CustomDot = ({ cx, cy, payload }) => {
    const color = payload.Profit >= 0 ? CHART_COLORS.sales : '#ff4d4f';
    return <circle cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />;
  };

  return (
    <Card style={CARD_STYLE} styles={{ body: CARD_BODY }}>
      <ChartLabel text="Profit by Year" />
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={148}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={shortInr} tick={{ fontSize: 9 }} />
            <Tooltip content={<ChartTooltip />} />
            <ReferenceLine y={0} stroke="#ddd" strokeDasharray="4 2" />
            <Line
              type="monotone" dataKey="Profit"
              stroke={CHART_COLORS.profit} strokeWidth={2}
              dot={<CustomDot />} activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ height: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 12 }}>No data</div>
      )}
    </Card>
  );
}
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i), label: String(currentYear - i),
}));

function SummaryCard({ label, value, color, bg, prefix = '' }) {
  return (
    <Card style={{ borderRadius: 14, background: bg, border: 'none', height: '100%' }} styles={{ body: { padding: '16px 20px' } }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, letterSpacing: -0.5 }}>
        {prefix}{formatCurrency(Math.abs(value))}
      </div>
    </Card>
  );
}

function CountCard({ icon, label, count, sub, color, bg, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: bg, border: `1px solid ${color}22`, borderRadius: 14,
        padding: '14px 16px', cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.15s',
        flex: 1, minWidth: 130,
      }}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.boxShadow = `0 4px 16px ${color}33`; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {typeof icon === 'string' ? (
            <div style={{
              width: 22, height: 22,
              backgroundColor: color,
              WebkitMask: `url(${icon}) no-repeat center / contain`,
              mask: `url(${icon}) no-repeat center / contain`,
            }} />
          ) : (
            <span style={{ color, fontSize: 16 }}>{icon}</span>
          )}
        </div>
        <Text style={{ fontSize: 12, color: '#888', fontWeight: 500 }}>{label}</Text>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>
        {count === null ? <Spin size="small" /> : count}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#aaa', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [years, setYears]     = useState(() => {
    const saved = sessionStorage.getItem('dashboard_years');
    return saved ? JSON.parse(saved) : [String(currentYear)];
  });
  const [loading, setLoading] = useState(false);

  const [counts, setCounts]   = useState(null);
  const [totals, setTotals]   = useState({ expense: 0, selling: 0, profit: 0 });
  const [crops, setCrops]     = useState([]);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getDashboardCountApi({ years });
      const data = res.data?.data;
      if (data) {
        setCounts(data);
        const finance = data.finance || [];
        const expense = finance.reduce((s, f) => s + (Number(f.total_expense) || 0), 0);
        const selling = finance.reduce((s, f) => s + (Number(f.total_sales)   || 0), 0);
        const profit  = finance.reduce((s, f) => s + (Number(f.profit)        || 0), 0);
        setTotals({ expense, selling, profit });
      }
    } catch {} finally { setLoading(false); }
  }, [years]);

  useEffect(() => { sessionStorage.setItem('dashboard_years', JSON.stringify(years)); }, [years]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    const uid = getUserId();
    if (!uid) return;
    getRecentCropsApi(uid)
      .then((res) => setCrops(res.data?.data || res.data || []))
      .catch(() => {});
  }, []);

  const recentCrops = [...crops].reverse().slice(0, 10);

  const farmCount    = counts?.farms?.total    ?? null;
  const plotCount    = counts?.plots?.total    ?? null;
  const cropCount    = counts?.crops?.total    ?? null;
  const activeCrops  = counts?.crops?.active   ?? 0;
  const harvestedCrops = counts?.crops?.harvested ?? 0;
  const accountCount = counts?.accounts?.total ?? null;
  const accountSub   = counts?.accounts
    ? [
        `Labour ${counts.accounts.labour ?? 0}`,
        `Shop ${counts.accounts.shop_keeper ?? 0}`,
        `Buyer ${counts.accounts.buyer ?? 0}`,
        `General ${counts.accounts.general ?? 0}`,
      ].join(' · ')
    : undefined;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconDashboard}) no-repeat center / contain`, mask: `url(${iconDashboard}) no-repeat center / contain`, flexShrink: 0 }} />
          Dashboard
        </Title>
        <Space wrap>
          <Select
            mode="multiple" value={years} onChange={setYears}
            options={YEAR_OPTIONS} placeholder="Year" style={{ minWidth: 160 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchCounts} loading={loading} />
        </Space>
      </div>

      {/* Row 1: Count cards */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px', marginBottom: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Counts</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <CountCard icon={iconFarm}    label="Farms"    count={farmCount}    color="#1a6b2a" bg="#f6ffed" onClick={() => navigate('/farms', { state: { years } })} />
          <CountCard icon={iconPlot}    label="Plots"    count={plotCount}    color="#722ed1" bg="#f9f0ff" onClick={() => navigate('/plots')} />
          <CountCard
            icon={iconCrop} label="Crops" count={cropCount}
            sub={`${activeCrops} active · ${harvestedCrops} harvested`}
            color="#fa8c16" bg="#fff7e6" onClick={() => navigate('/crops', { state: { years } })}
          />
          <CountCard icon={iconAccount} label="Accounts" count={accountCount} sub={accountSub} color="#1677ff" bg="#e6f4ff" onClick={() => navigate('/accounts')} />
        </div>
      </div>

      {/* Finance container: totals + charts */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px 16px 12px', marginBottom: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>Finance</div>
        {/* Summary totals */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <SummaryCard label="Total Expense" value={totals.expense} color="#fa8c16" bg="#fff7e6" />
          </Col>
          <Col xs={24} sm={8}>
            <SummaryCard label="Total Sales" value={totals.selling} color="#52c41a" bg="#f6ffed" />
          </Col>
          <Col xs={24} sm={8}>
            <SummaryCard
              label={totals.profit >= 0 ? 'Net Profit' : 'Net Loss'} value={totals.profit}
              color={totals.profit >= 0 ? '#52c41a' : '#ff4d4f'}
              bg={totals.profit >= 0 ? '#f6ffed' : '#fff1f0'}
              prefix={totals.profit < 0 ? '-' : ''}
            />
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={[12, 12]} align="stretch">
          <Col xs={24} sm={12}>
            <ExpenseSalesChart years={years} finance={counts?.finance || []} expense={totals.expense} sales={totals.selling} />
          </Col>
          <Col xs={24} sm={12}>
            <ProfitLineChart finance={counts?.finance || []} />
          </Col>
        </Row>
      </div>

      {/* Row 4: Recent crops */}
      {recentCrops.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Text strong style={{ display: 'block', marginBottom: 10, color: '#555', fontSize: 13 }}>🌱 Recent Crops</Text>
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
            {recentCrops.map((crop) => {
              const isActive   = crop.status === 1 || crop.status === '1';
              const imgSrc     = crop.photo      ? CROP_PHOTO(crop.photo)
                               : crop.image      ? CROP_NAME_IMAGE(crop.image)
                               : crop.crop_image ? CROP_NAME_IMAGE(crop.crop_image)
                               : crop.name_image ? CROP_NAME_IMAGE(crop.name_image)
                               : null;
              const expense    = Number(crop.kharch || crop.totalExpanse || crop.total_expanse || 0);
              const income     = Number(crop.vechan || crop.totalSelling || crop.total_selling || 0);
              const cropProfit = income - expense;
              return (
                <div
                  key={crop.id}
                  onClick={() => navigate(`/farms/${crop.farm_id ?? crop.khetarId}/farm/${crop.id}/expenses`)}
                  style={{
                    flexShrink: 0, width: 160, background: '#fff', borderRadius: 14,
                    overflow: 'hidden', border: '1px solid #f0f0f0', cursor: 'pointer',
                    boxShadow: '0 1px 5px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 18px rgba(26,107,42,0.15)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 5px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'none'; }}
                >
                  {imgSrc
                    ? <img src={imgSrc} alt={crop.crop_name || crop.crop} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: 90, background: 'linear-gradient(135deg,#f6ffed,#d9f7be)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>🌾</div>
                  }
                  <div style={{ padding: '8px 10px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, marginRight: 4 }}>
                        {crop.crop_name || crop.crop}
                      </div>
                      <Tag color={isActive ? 'green' : 'orange'} style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}>
                        {isActive ? 'Active' : 'Harvested'}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <Text type="secondary">Expense</Text>
                        <span style={{ color: '#fa8c16', fontWeight: 600 }}>₹{expense.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <Text type="secondary">Income</Text>
                        <span style={{ color: '#52c41a', fontWeight: 600 }}>₹{income.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, borderTop: '1px solid #f0f0f0', paddingTop: 4, marginTop: 2 }}>
                        <Text type="secondary">Profit</Text>
                        <span style={{ color: cropProfit >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 700 }}>
                          {cropProfit < 0 ? '-' : ''}₹{Math.abs(cropProfit).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
